import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { siteConfig } from "@/config/siteConfig";
import {
  findSolution,
  ASSESSMENT_STEPS,
} from "@/data/solutionsData";
import { productsBySolutionSlug } from "@/data/productsData";
import CodingMarkingExtras from "./CodingMarkingExtras";

const IMG_BASE = "/images/home";

const SolutionHeroImage = ({ imageKey, alt }) => (
  <picture>
    <source
      type="image/webp"
      srcSet={`${IMG_BASE}/${imageKey}-800.webp 800w, ${IMG_BASE}/${imageKey}.webp 1264w`}
      sizes="(min-width: 1024px) 560px, 100vw"
    />
    <img
      src={`${IMG_BASE}/${imageKey}.jpg`}
      alt={alt}
      loading="eager"
      decoding="async"
      width="1264"
      height="848"
      data-placeholder-image="true"
      className="w-full h-auto"
    />
  </picture>
);

const SolutionLandingPage = ({ slug }) => {
  const solution = findSolution(slug);
  if (!solution) return null;

  const path = `/solutions/${solution.slug}`;
  const canonical = `${siteConfig.DOMAIN}${path}`;
  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: solution.name,
    description: solution.seoDescription,
    url: canonical,
  };
  const products = productsBySolutionSlug(solution.productSolutionSlug);

  return (
    <>
      <SEO
        rawTitle={`${solution.name} | ${siteConfig.COMPANY_NAME}`}
        description={solution.seoDescription}
        path={path}
        extraSchema={webPageSchema}
      />
      <Helmet>
      </Helmet>

      <div className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10">
        <Breadcrumbs
          items={[
            { label: "Home", to: "/" },
            { label: "Solutions", to: "/solutions/blister-printing" },
            { label: solution.name },
          ]}
        />
      </div>

      {/* Hero */}
      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10 pb-16 lg:pb-24"
        data-testid={`solution-hero-${solution.slug}`}
      >
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] gap-12 lg:gap-16 items-center">
          <div>
            <p className="eyebrow">{solution.eyebrow}</p>
            <h1
              className="font-heading mt-4 text-[32px] leading-tight lg:text-[46px] lg:leading-[1.05] font-semibold text-[var(--navy)]"
              data-testid="solution-h1"
            >
              {solution.name}
            </h1>
            <div className="mt-6 space-y-4 text-[16px] leading-relaxed text-[var(--charcoal)] max-w-xl">
              {solution.intro.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            {solution.note && (
              <p className="mt-6 text-[13px] italic text-[var(--slate)] max-w-xl">
                {solution.note}
              </p>
            )}
          </div>
          <div className="image-frame">
            <SolutionHeroImage
              imageKey={solution.heroImageKey}
              alt={solution.heroAlt}
            />
          </div>
        </div>
      </section>

      {/* Optional extras before products (coding-marking special) */}
      {solution.special === "coding-marking" && <CodingMarkingExtras.AppIcons />}

      {/* Products in this solution */}
      {products.length > 0 && (
        <section
          className="mx-auto max-w-[1240px] px-6 lg:px-10 py-14 lg:py-20 border-t border-[var(--border)]"
          data-testid="solution-products"
        >
          <h2 className="font-heading text-2xl lg:text-3xl font-semibold text-[var(--navy)]">
            Products in this solution
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p) => (
              <article
                key={p.slug}
                className="border border-[var(--border)] rounded-[4px] p-6 flex flex-col bg-white"
                data-testid={`solution-product-${p.slug}`}
              >
                <p
                  className="text-[12px] uppercase tracking-[0.12em] font-semibold text-[var(--pharma-blue)]"
                >
                  {p.categoryLabel}
                </p>
                <h3 className="font-heading mt-3 text-[19px] font-semibold text-[var(--navy)]">
                  {p.name}
                </h3>
                <p className="mt-3 text-[14.5px] leading-relaxed text-[var(--slate)] flex-1">
                  {p.shortDescription.split(". ")[0]}.
                </p>
                <div className="mt-5 flex flex-wrap items-center gap-4">
                  <Link
                    to={`/products/${p.slug}`}
                    className="arrow-link"
                    data-testid={`solution-product-${p.slug}-details`}
                  >
                    View Product <ArrowRight className="w-4 h-4" />
                  </Link>
                  {solution.special === "blister-printing" && (
                    <Link
                      to={
                        p.slug === "blister-pack-violet-ink"
                          ? `/request-sample?product=${p.slug}`
                          : `/contact?type=technical&product=${p.slug}`
                      }
                      className="text-[13px] font-semibold text-[var(--navy)] underline underline-offset-4 hover:text-[var(--pharma-blue)]"
                      data-testid={`solution-product-${p.slug}-secondary`}
                    >
                      {p.slug === "blister-pack-violet-ink"
                        ? "Request Sample"
                        : "Discuss Application"}
                    </Link>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* Assessment framework — 4 steps */}
      <section
        className="bg-[var(--bg-alt)]"
        data-testid="solution-assessment"
      >
        <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-14 lg:py-20">
          <p className="eyebrow">Working Together</p>
          <h2 className="font-heading mt-4 text-2xl lg:text-3xl font-semibold text-[var(--navy)]">
            Application assessment to commercial supply
          </h2>
          <div className="mt-12 hidden lg:flex items-start justify-between gap-4 relative">
            <div
              className="absolute top-6 left-[12%] right-[12%] h-px"
              style={{ background: "var(--pharma-blue)", opacity: 0.35 }}
              aria-hidden="true"
            />
            {ASSESSMENT_STEPS.map((s, i) => (
              <div
                key={s}
                className="relative flex flex-col items-center text-center flex-1 z-10"
                data-testid={`assessment-step-${i + 1}`}
              >
                <div
                  className="w-12 h-12 rounded-full bg-white border-2 flex items-center justify-center"
                  style={{ borderColor: "var(--pharma-blue)" }}
                >
                  <span className="font-heading text-[16px] font-semibold text-[var(--navy)]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <p className="font-heading mt-4 text-[15px] font-semibold text-[var(--navy)] max-w-[180px]">
                  {s}
                </p>
              </div>
            ))}
          </div>
          <ol className="mt-8 lg:hidden space-y-4 relative">
            <div
              className="absolute left-6 top-6 bottom-6 w-px"
              style={{ background: "var(--pharma-blue)", opacity: 0.35 }}
              aria-hidden="true"
            />
            {ASSESSMENT_STEPS.map((s, i) => (
              <li key={s} className="flex items-start gap-4 relative z-10">
                <div
                  className="w-12 h-12 shrink-0 rounded-full bg-white border-2 flex items-center justify-center"
                  style={{ borderColor: "var(--pharma-blue)" }}
                >
                  <span className="font-heading text-[15px] font-semibold text-[var(--navy)]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <p className="font-heading pt-3 text-[15px] font-semibold text-[var(--navy)]">
                  {s}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Special: coding-marking qualification form */}
      {solution.special === "coding-marking" && <CodingMarkingExtras.Form />}

      {/* Closing CTA band */}
      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-20"
        data-testid="solution-cta-band"
      >
        <div className="border border-[var(--border)] rounded-[4px] p-8 lg:p-12">
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6 lg:gap-10">
            <div className="flex-1">
              <h2 className="font-heading text-xl lg:text-2xl font-semibold text-[var(--navy)]">
                Have a specific application for this solution?
              </h2>
              <p className="mt-3 text-[15px] text-[var(--charcoal)] max-w-2xl">
                Share your machine, substrate and print requirement — our team
                will review whether an appropriate product is available for
                evaluation.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to={`/request-sample?solution=${solution.slug}`}
                className="btn-primary text-sm"
                data-testid="solution-cta-sample"
              >
                Request a Sample
              </Link>
              <Link
                to={`/contact?type=technical&solution=${solution.slug}`}
                className="btn-outline text-sm"
                data-testid="solution-cta-talk"
              >
                Discuss Your Application
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SolutionLandingPage;
